import { EventTypes, TYPES } from '../../render/constants';
import { Rect } from '../region';

// Simple parser that extracts linear anchors (M/L/H/V/Z). If curves exist, returns null.
export function parseLinearAnchors(pathData) {
  if (!pathData || typeof pathData !== 'string') return null;

  const commands = pathData.match(/[MLHVZmlhvz][^MLHVZmlhvz]*/g) || [];
  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  const anchors = [];
  let isClosed = false;

  function pushAnchor(x, y) {
    anchors.push({ x, y });
  }

  for (const cmd of commands) {
    const type = cmd[0];
    const coords = cmd.slice(1).trim();
    const isAbsolute = type === type.toUpperCase();
    // Only numbers allowed in our subset
    const numbers = coords ? (coords.match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi) || []).map(Number) : [];

    switch (type.toLowerCase()) {
      case 'm': {
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            const x = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];
            currentX = x;
            currentY = y;
            if (anchors.length === 0) {
              startX = x;
              startY = y;
              pushAnchor(x, y);
            } else {
              // Subsequent moveto pairs are treated as lineto
              pushAnchor(x, y);
            }
          }
        }
        break;
      }
      case 'l': {
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            const x = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];
            currentX = x;
            currentY = y;
            pushAnchor(x, y);
          }
        }
        break;
      }
      case 'h': {
        for (let i = 0; i < numbers.length; i++) {
          const x = isAbsolute ? numbers[i] : currentX + numbers[i];
          currentX = x;
          pushAnchor(currentX, currentY);
        }
        break;
      }
      case 'v': {
        for (let i = 0; i < numbers.length; i++) {
          const y = isAbsolute ? numbers[i] : currentY + numbers[i];
          currentY = y;
          pushAnchor(currentX, currentY);
        }
        break;
      }
      case 'z': {
        isClosed = true;
        // closing doesn't add a new anchor beyond connecting back to start
        currentX = startX;
        currentY = startY;
        break;
      }
      default:
        // Contains curves or unsupported commands -> bail out
        return null;
    }
  }

  if (anchors.length < 2) return null;
  return { anchors, closed: isClosed };
}

export function buildPathFromAnchors(anchors, closed = false) {
  if (!anchors || anchors.length === 0) return '';
  const parts = [];
  parts.push(`M ${anchors[0].x} ${anchors[0].y}`);
  for (let i = 1; i < anchors.length; i++) {
    parts.push(`L ${anchors[i].x} ${anchors[i].y}`);
  }
  if (closed) parts.push('Z');
  return parts.join(' ');
}

// --- Curve-capable parser and editor helpers ---

export function parsePathSegments(pathData) {
  if (!pathData || typeof pathData !== 'string') return null;
  const tokens = pathData.match(/[a-zA-Z]|-?\d*\.?\d+(?:e[+-]?\d+)?/gi) || [];
  let i = 0;
  function nextIsNumber() {
    return i < tokens.length && /^-?\d/.test(tokens[i]);
  }
  function nextNumber() {
    return parseFloat(tokens[i++]);
  }
  function nextCommand() {
    return tokens[i++];
  }

  let cmd = null;
  let current = { x: 0, y: 0 };
  let start = { x: 0, y: 0 };
  let prevCubicCtrl = null; // for S
  let prevQuadCtrl = null; // for T

  const anchors = [];
  const segments = [];
  let currentAnchorIndex = -1;

  function ensureAnchor(p) {
    currentAnchorIndex++;
    anchors[currentAnchorIndex] = { x: p.x, y: p.y };
    return currentAnchorIndex;
  }

  function addLinear(to) {
    const fromIndex = currentAnchorIndex;
    const toIndex = ensureAnchor(to);
    segments.push({ type: 'L', from: fromIndex, to: toIndex });
    current = { ...to };
    prevCubicCtrl = null;
    prevQuadCtrl = null;
  }

  function addCubic(c1, c2, to) {
    const fromIndex = currentAnchorIndex;
    const toIndex = ensureAnchor(to);
    segments.push({ type: 'C', from: fromIndex, to: toIndex, c1: { ...c1 }, c2: { ...c2 } });
    current = { ...to };
    prevCubicCtrl = { ...c2 }; // last explicit control for S reflection
    prevQuadCtrl = null;
  }

  function addQuad(c, to) {
    const fromIndex = currentAnchorIndex;
    const toIndex = ensureAnchor(to);
    segments.push({ type: 'Q', from: fromIndex, to: toIndex, c: { ...c } });
    current = { ...to };
    prevQuadCtrl = { ...c };
    prevCubicCtrl = null;
  }

  function addArc(rx, ry, angle, largeArcFlag, sweepFlag, to) {
    const fromIndex = currentAnchorIndex;
    const toIndex = ensureAnchor(to);
    segments.push({
      type: 'A',
      from: fromIndex,
      to: toIndex,
      rx,
      ry,
      angle,
      large: largeArcFlag ? 1 : 0,
      sweep: sweepFlag ? 1 : 0,
    });
    current = { ...to };
    prevCubicCtrl = null;
    prevQuadCtrl = null;
  }

  let closed = false;

  while (i < tokens.length) {
    if (/[a-zA-Z]/.test(tokens[i])) cmd = nextCommand();
    if (!cmd) break;
    const absolute = cmd === cmd.toUpperCase();
    const c = cmd.toUpperCase();
    switch (c) {
      case 'M': {
        const x = nextNumber();
        const y = nextNumber();
        const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
        current = p;
        start = p;
        currentAnchorIndex = -1;
        ensureAnchor(p);
        prevCubicCtrl = null;
        prevQuadCtrl = null;
        // Subsequent pairs are implicit L
        while (nextIsNumber()) {
          const lx = nextNumber();
          const ly = nextNumber();
          const q = absolute ? { x: lx, y: ly } : { x: current.x + lx, y: current.y + ly };
          addLinear(q);
        }
        break;
      }
      case 'L': {
        while (nextIsNumber()) {
          const x = nextNumber();
          const y = nextNumber();
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addLinear(p);
        }
        break;
      }
      case 'H': {
        while (nextIsNumber()) {
          const x = nextNumber();
          const p = absolute ? { x, y: current.y } : { x: current.x + x, y: current.y };
          addLinear(p);
        }
        break;
      }
      case 'V': {
        while (nextIsNumber()) {
          const y = nextNumber();
          const p = absolute ? { x: current.x, y } : { x: current.x, y: current.y + y };
          addLinear(p);
        }
        break;
      }
      case 'C': {
        while (nextIsNumber()) {
          const x1 = nextNumber(),
            y1 = nextNumber();
          const x2 = nextNumber(),
            y2 = nextNumber();
          const x = nextNumber(),
            y = nextNumber();
          const c1 = absolute ? { x: x1, y: y1 } : { x: current.x + x1, y: current.y + y1 };
          const c2 = absolute ? { x: x2, y: y2 } : { x: current.x + x2, y: current.y + y2 };
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addCubic(c1, c2, p);
        }
        break;
      }
      case 'S': {
        while (nextIsNumber()) {
          const x2 = nextNumber(),
            y2 = nextNumber();
          const x = nextNumber(),
            y = nextNumber();
          let c1;
          if (prevCubicCtrl) {
            c1 = {
              x: 2 * current.x - prevCubicCtrl.x,
              y: 2 * current.y - prevCubicCtrl.y,
            };
          } else {
            c1 = { x: current.x, y: current.y };
          }
          const c2 = absolute ? { x: x2, y: y2 } : { x: current.x + x2, y: current.y + y2 };
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addCubic(c1, c2, p);
        }
        break;
      }
      case 'Q': {
        while (nextIsNumber()) {
          const x1 = nextNumber(),
            y1 = nextNumber();
          const x = nextNumber(),
            y = nextNumber();
          const cq = absolute ? { x: x1, y: y1 } : { x: current.x + x1, y: current.y + y1 };
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addQuad(cq, p);
        }
        break;
      }
      case 'A': {
        while (nextIsNumber()) {
          const rx = nextNumber();
          const ry = nextNumber();
          const ang = nextNumber();
          const laf = nextNumber();
          const swf = nextNumber();
          const x = nextNumber();
          const y = nextNumber();
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addArc(Math.abs(rx), Math.abs(ry), ang, laf ? 1 : 0, swf ? 1 : 0, p);
        }
        break;
      }
      case 'T': {
        while (nextIsNumber()) {
          const x = nextNumber(),
            y = nextNumber();
          let cq;
          if (prevQuadCtrl) {
            cq = {
              x: 2 * current.x - prevQuadCtrl.x,
              y: 2 * current.y - prevQuadCtrl.y,
            };
          } else {
            cq = { x: current.x, y: current.y };
          }
          const p = absolute ? { x, y } : { x: current.x + x, y: current.y + y };
          addQuad(cq, p);
        }
        break;
      }
      case 'Z': {
        closed = true;
        current = { ...start };
        i++; // move past 'Z'
        break;
      }
      default: {
        // Unsupported commands: bail out to linear-only mode
        return null;
      }
    }
  }

  return { anchors, segments, closed };
}

export function buildPathFromSegments(anchors, segments, closed = false) {
  if (!anchors || anchors.length === 0) return '';
  const parts = [];
  parts.push(`M ${anchors[0].x} ${anchors[0].y}`);
  for (const seg of segments) {
    const to = anchors[seg.to];
    if (seg.type === 'L') {
      parts.push(`L ${to.x} ${to.y}`);
    } else if (seg.type === 'C') {
      parts.push(`C ${seg.c1.x} ${seg.c1.y} ${seg.c2.x} ${seg.c2.y} ${to.x} ${to.y}`);
    } else if (seg.type === 'Q') {
      parts.push(`Q ${seg.c.x} ${seg.c.y} ${to.x} ${to.y}`);
    } else if (seg.type === 'A') {
      parts.push(
        `A ${seg.rx} ${seg.ry} ${seg.angle} ${seg.large ? 1 : 0} ${seg.sweep ? 1 : 0} ${to.x} ${to.y}`
      );
    }
  }
  if (closed) parts.push('Z');
  return parts.join(' ');
}

export function splitSegmentAtMidpoint(anchors, segments, segIndex) {
  const seg = segments[segIndex];
  if (!seg) return { anchors, segments };
  const from = anchors[seg.from];
  const to = anchors[seg.to];
  if (seg.type === 'L') {
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    const newAnchorIndex = anchors.length;
    anchors = anchors.slice();
    segments = segments.slice();
    anchors.push(mid);
    // Replace original with two L segments
    const left = { type: 'L', from: seg.from, to: newAnchorIndex };
    const right = { type: 'L', from: newAnchorIndex, to: seg.to };
    segments.splice(segIndex, 1, left, right);
    return { anchors, segments };
  }
  if (seg.type === 'C') {
    const P0 = from,
      P1 = seg.c1,
      P2 = seg.c2,
      P3 = to;
    const A = { x: (P0.x + P1.x) / 2, y: (P0.y + P1.y) / 2 };
    const B = { x: (P1.x + P2.x) / 2, y: (P1.y + P2.y) / 2 };
    const Cc = { x: (P2.x + P3.x) / 2, y: (P2.y + P3.y) / 2 };
    const D = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
    const E = { x: (B.x + Cc.x) / 2, y: (B.y + Cc.y) / 2 };
    const F = { x: (D.x + E.x) / 2, y: (D.y + E.y) / 2 };
    const newAnchorIndex = anchors.length;
    anchors = anchors.slice();
    segments = segments.slice();
    anchors.push(F);
    const left = { type: 'C', from: seg.from, to: newAnchorIndex, c1: A, c2: D };
    const right = { type: 'C', from: newAnchorIndex, to: seg.to, c1: E, c2: Cc };
    segments.splice(segIndex, 1, left, right);
    return { anchors, segments };
  }
  if (seg.type === 'Q') {
    const P0 = from,
      Q = seg.c,
      P2 = to;
    const A = { x: (P0.x + Q.x) / 2, y: (P0.y + Q.y) / 2 };
    const B = { x: (Q.x + P2.x) / 2, y: (Q.y + P2.y) / 2 };
    const F = { x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 };
    const newAnchorIndex = anchors.length;
    anchors = anchors.slice();
    segments = segments.slice();
    anchors.push(F);
    const left = { type: 'Q', from: seg.from, to: newAnchorIndex, c: A };
    const right = { type: 'Q', from: newAnchorIndex, to: seg.to, c: B };
    segments.splice(segIndex, 1, left, right);
    return { anchors, segments };
  }
  if (seg.type === 'A') {
    // Convert arc midpoint into a quadratic approximation split for editing simplicity.
    // This is an approximation: replace arc with two quadratics.
    const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
    const newAnchorIndex = anchors.length;
    anchors = anchors.slice();
    segments = segments.slice();
    anchors.push(mid);
    // naive approximate control points along chord normal
    const nx = to.y - from.y;
    const ny = -(to.x - from.x);
    const len = Math.hypot(nx, ny) || 1;
    const ux = nx / len;
    const uy = ny / len;
    const k = 0.001 * (seg.rx + seg.ry); // tiny offset to avoid visual jump
    const cLeft = { x: (from.x + mid.x) / 2 + ux * k, y: (from.y + mid.y) / 2 + uy * k };
    const cRight = { x: (mid.x + to.x) / 2 + ux * k, y: (mid.y + to.y) / 2 + uy * k };
    const left = { type: 'Q', from: seg.from, to: newAnchorIndex, c: cLeft };
    const right = { type: 'Q', from: newAnchorIndex, to: seg.to, c: cRight };
    segments.splice(segIndex, 1, left, right);
    return { anchors, segments };
  }
  return { anchors, segments };
}

// Prepare cache for editing
export function preparePathEditCache(pathData) {
  const parsed = parsePathSegments(pathData);
  if (!parsed) return null;
  return parsed; // { anchors, segments, closed }
}

export function applyMoveAnchor(cache, pointIndex, dx, dy) {
  // Move the anchor
  const anchors = cache.anchors.map((p, idx) =>
    idx === pointIndex ? { x: p.x + dx, y: p.y + dy } : { ...p }
  );
  // Move any control points attached to this anchor to preserve local shape
  const segments = cache.segments.map((s) => {
    if (s.type === 'C') {
      const moved = { ...s, c1: { ...s.c1 }, c2: { ...s.c2 } };
      if (s.from === pointIndex) {
        moved.c1 = { x: s.c1.x + dx, y: s.c1.y + dy };
      }
      if (s.to === pointIndex) {
        moved.c2 = { x: s.c2.x + dx, y: s.c2.y + dy };
      }
      return moved;
    } else if (s.type === 'Q') {
      // Quadratic has a single control point; shift it when either end moves
      if (s.from === pointIndex || s.to === pointIndex) {
        return { ...s, c: { x: s.c.x + dx, y: s.c.y + dy } };
      }
      return { ...s };
    }
    return { ...s };
  });
  const path = buildPathFromSegments(anchors, segments, cache.closed);
  return { anchors, segments, closed: cache.closed, path };
}

export function applyMoveControl(cache, segmentIndex, role, dx, dy, options = {}) {
  const anchors = cache.anchors.slice();
  const symmetric = !!options.symmetric;

  // Clone segments deeply enough for control edits
  const segments = cache.segments.map((s) => {
    if (s.type === 'C') return { ...s, c1: { ...s.c1 }, c2: { ...s.c2 } };
    if (s.type === 'Q') return { ...s, c: { ...s.c } };
    return { ...s };
  });

  const seg = segments[segmentIndex];
  if (!seg) {
    const path = buildPathFromSegments(anchors, segments, cache.closed);
    return { anchors, segments, closed: cache.closed, path };
  }

  if (seg.type === 'C') {
    if (role === 'c1') {
      seg.c1 = { x: seg.c1.x + dx, y: seg.c1.y + dy };
      if (symmetric) {
        // Mirror incoming handle of previous segment around this anchor
        const anchor = anchors[seg.from];
        // Find previous segment that ends at this anchor
        let prevIndex = -1;
        for (let i = segments.length - 1; i >= 0; i--) {
          if (i === segmentIndex) continue;
          const s = segments[i];
          if (s.to === seg.from) {
            prevIndex = i;
            break;
          }
        }
        if (prevIndex >= 0 && segments[prevIndex].type === 'C') {
          const prevSeg = segments[prevIndex];
          const vx = seg.c1.x - anchor.x;
          const vy = seg.c1.y - anchor.y;
          prevSeg.c2 = { x: anchor.x - vx, y: anchor.y - vy };
        }
      }
    } else if (role === 'c2') {
      seg.c2 = { x: seg.c2.x + dx, y: seg.c2.y + dy };
      if (symmetric) {
        // Mirror outgoing handle of next segment around the to-anchor
        const anchor = anchors[seg.to];
        // Find next segment that starts at this anchor
        let nextIndex = -1;
        for (let i = 0; i < segments.length; i++) {
          if (i === segmentIndex) continue;
          const s = segments[i];
          if (s.from === seg.to) {
            nextIndex = i;
            break;
          }
        }
        if (nextIndex >= 0 && segments[nextIndex].type === 'C') {
          const nextSeg = segments[nextIndex];
          const vx = seg.c2.x - anchor.x;
          const vy = seg.c2.y - anchor.y;
          nextSeg.c1 = { x: anchor.x - vx, y: anchor.y - vy };
        }
      }
    }
  } else if (seg.type === 'Q') {
    if (role === 'q') {
      seg.c = { x: seg.c.x + dx, y: seg.c.y + dy };
    }
  }

  const path = buildPathFromSegments(anchors, segments, cache.closed);
  return { anchors, segments, closed: cache.closed, path };
}

// --- Geometry Helpers for Hit Testing ---

function distToSegmentSquared(p, v, w) {
  const l2 = (v.x - w.x) ** 2 + (v.y - w.y) ** 2;
  if (l2 === 0) return (p.x - v.x) ** 2 + (p.y - v.y) ** 2;
  let t = ((p.x - v.x) * (w.x - v.x) + (p.y - v.y) * (w.y - v.y)) / l2;
  t = Math.max(0, Math.min(1, t));
  return (p.x - (v.x + t * (w.x - v.x))) ** 2 + (p.y - (v.y + t * (w.y - v.y))) ** 2;
}

function isNearSegment(p, a, b, threshold) {
  return distToSegmentSquared(p, a, b) < threshold * threshold;
}

function isNearBezier(p, p0, p1, p2, p3, threshold) {
  // Recursive subdivision or coarse sampling
  // Sampling 10 points is usually enough for UI hit testing
  const steps = 10;
  let prev = p0;
  const tSq = threshold * threshold;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const it = 1 - t;
    const curr = {
      x: it * it * it * p0.x + 3 * it * it * t * p1.x + 3 * it * t * t * p2.x + t * t * t * p3.x,
      y: it * it * it * p0.y + 3 * it * it * t * p1.y + 3 * it * t * t * p2.y + t * t * t * p3.y,
    };
    if (distToSegmentSquared(p, prev, curr) < tSq) return true;
    prev = curr;
  }
  return false;
}

function isNearQuad(p, p0, c, p2, threshold) {
  const steps = 8;
  let prev = p0;
  const tSq = threshold * threshold;
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const it = 1 - t;
    const curr = {
      x: it * it * p0.x + 2 * it * t * c.x + t * t * p2.x,
      y: it * it * p0.y + 2 * it * t * c.y + t * t * p2.y,
    };
    if (distToSegmentSquared(p, prev, curr) < tSq) return true;
    prev = curr;
  }
  return false;
}

export function getInteractivePathHandlers(stageContext, node, region, scale = 1) {
  const parsed = parseLinearAnchors(node.path);
  const segmentsParsed = parsePathSegments(node.path);
  const handlers = [];
  if (!segmentsParsed && !parsed) return [];
  if (segmentsParsed) {
    const { anchors, segments } = segmentsParsed;
    if (!anchors || anchors.length === 0) return [];
    const controlPointSize = 12 * scale;

    const topLeft = region.topLeft;
    const topRight = region.topRight;
    const bottomLeft = region.bottomLeft;
    const bottomRight = region.bottomRight;
    // Compute local basis (origin and size) for normalization when node.w/h are missing
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    anchors.forEach((p) => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });
    segments.forEach((s) => {
      if (s.type === 'C') {
        minX = Math.min(minX, s.c1.x, s.c2.x);
        minY = Math.min(minY, s.c1.y, s.c2.y);
        maxX = Math.max(maxX, s.c1.x, s.c2.x);
        maxY = Math.max(maxY, s.c1.y, s.c2.y);
      } else if (s.type === 'Q') {
        minX = Math.min(minX, s.c.x);
        minY = Math.min(minY, s.c.y);
        maxX = Math.max(maxX, s.c.x);
        maxY = Math.max(maxY, s.c.y);
      }
    });
    const basisW = node.w && node.w > 0 ? node.w : Math.max(1, maxX - minX);
    const basisH = node.h && node.h > 0 ? node.h : Math.max(1, maxY - minY);
    const basisX = node.w && node.w > 0 ? 0 : minX;
    const basisY = node.h && node.h > 0 ? 0 : minY;

    function toRegionSpace(local) {
      const nx = basisW ? (local.x - basisX) / basisW : 0;
      const ny = basisH ? (local.y - basisY) / basisH : 0;
      const top = {
        x: topLeft.x + (topRight.x - topLeft.x) * nx,
        y: topLeft.y + (topRight.y - topLeft.y) * nx,
      };
      const bottom = {
        x: bottomLeft.x + (bottomRight.x - bottomLeft.x) * nx,
        y: bottomLeft.y + (bottomRight.y - bottomLeft.y) * nx,
      };
      return { x: top.x + (bottom.x - top.x) * ny, y: top.y + (bottom.y - top.y) * ny };
    }

    // Anchor handlers
    anchors.forEach((pt, index) => {
      const abs = toRegionSpace(pt);
      const rect = new Rect(
        abs.x - controlPointSize,
        abs.y - controlPointSize,
        controlPointSize * 2,
        controlPointSize * 2
      );
      rect.id = `pathPoint_${index}`;
      rect.type = 'pathPoint';
      rect.pointIndex = index;
      rect.localPoint = { ...pt };
      rect.node = node;
      handlers.push(rect);
    });

    // Control handlers and midpoints
    segments.forEach((seg, idx) => {
      let mid;
      let hitTest = () => false;

      if (seg.type === 'C') {
        const p0 = anchors[seg.from];
        const p3 = anchors[seg.to];
        const c1 = seg.c1;
        const c2 = seg.c2;
        const abs1 = toRegionSpace(c1);
        const abs2 = toRegionSpace(c2);
        const absP0 = toRegionSpace(p0);
        const absP3 = toRegionSpace(p3);

        const r1 = new Rect(
          abs1.x - controlPointSize * 0.9,
          abs1.y - controlPointSize * 0.9,
          controlPointSize * 1.8,
          controlPointSize * 1.8
        );
        r1.id = `pathCtrl_${idx}_c1`;
        r1.type = 'pathControl';
        r1.segmentIndex = idx;
        r1.controlRole = 'c1';
        r1.node = node;
        r1.anchorPoint = absP0;
        handlers.push(r1);

        const r2 = new Rect(
          abs2.x - controlPointSize * 0.9,
          abs2.y - controlPointSize * 0.9,
          controlPointSize * 1.8,
          controlPointSize * 1.8
        );
        r2.id = `pathCtrl_${idx}_c2`;
        r2.type = 'pathControl';
        r2.segmentIndex = idx;
        r2.controlRole = 'c2';
        r2.node = node;
        r2.anchorPoint = absP3;
        handlers.push(r2);

        mid = toRegionSpace({
          x: (p0.x + 3 * (c1.x + c2.x) + p3.x) / 8,
          y: (p0.y + 3 * (c1.y + c2.y) + p3.y) / 8,
        });

        hitTest = (x, y) => isNearBezier({ x, y }, absP0, abs1, abs2, absP3, 8 * scale);
      } else if (seg.type === 'Q') {
        const p0 = anchors[seg.from];
        const p2 = anchors[seg.to];
        const cq = seg.c;
        const absc = toRegionSpace(cq);
        const absP0 = toRegionSpace(p0);
        const absP2 = toRegionSpace(p2);

        const rc = new Rect(
          absc.x - controlPointSize * 0.9,
          absc.y - controlPointSize * 0.9,
          controlPointSize * 1.8,
          controlPointSize * 1.8
        );
        rc.id = `pathCtrl_${idx}_q`;
        rc.type = 'pathControl';
        rc.segmentIndex = idx;
        rc.controlRole = 'q';
        rc.node = node;
        rc.anchorPoint = absP0;
        handlers.push(rc);

        mid = toRegionSpace({
          x: (p0.x + 2 * cq.x + p2.x) / 4,
          y: (p0.y + 2 * cq.y + p2.y) / 4,
        });
        hitTest = (x, y) => isNearQuad({ x, y }, absP0, absc, absP2, 8 * scale);
      } else if (seg.type === 'L') {
        const p0 = anchors[seg.from];
        const p1 = anchors[seg.to];
        const absP0 = toRegionSpace(p0);
        const absP1 = toRegionSpace(p1);

        mid = toRegionSpace({ x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 });
        hitTest = (x, y) => isNearSegment({ x, y }, absP0, absP1, 8 * scale);
      } else if (seg.type === 'A') {
        const p0 = anchors[seg.from];
        const p1 = anchors[seg.to];
        const absP0 = toRegionSpace(p0);
        const absP1 = toRegionSpace(p1);

        mid = toRegionSpace({ x: (p0.x + p1.x) / 2, y: (p0.y + p1.y) / 2 });
        hitTest = (x, y) => isNearSegment({ x, y }, absP0, absP1, 8 * scale);
      }

      if (mid) {
        // Create a custom handler that is duck-typed as a Region/Rect
        // but overrides `contains` to check proximity to the stroke
        const handler = {
          id: `pathMidPoint_${idx}`,
          type: 'pathMidPoint',
          segmentIndex: idx,
          node: node,
          // Center for rendering the dot
          center: { x: mid.x, y: mid.y },
          // Standard w/h for rendering size reference (visual)
          w: controlPointSize * 2,
          h: controlPointSize * 2,
          // Custom hit test
          contains: (x, y) => hitTest(x, y),
        };
        handlers.push(handler);
      }
    });

    return handlers;
  }

  // Fallback to linear-only
  if (parsed) {
    const { anchors } = parsed;
    if (!anchors || anchors.length < 2) return [];
    const controlPointSize = 12 * scale;
    const topLeft = region.topLeft;
    const topRight = region.topRight;
    const bottomLeft = region.bottomLeft;
    const bottomRight = region.bottomRight;
    function toRegionSpace(local) {
      const nx = node.w ? local.x / node.w : 0;
      const ny = node.h ? local.y / node.h : 0;
      const top = {
        x: topLeft.x + (topRight.x - topLeft.x) * nx,
        y: topLeft.y + (topRight.y - topLeft.y) * nx,
      };
      const bottom = {
        x: bottomLeft.x + (bottomRight.x - bottomLeft.x) * nx,
        y: bottomLeft.y + (bottomRight.y - bottomLeft.y) * nx,
      };
      return { x: top.x + (bottom.x - top.x) * ny, y: top.y + (bottom.y - top.y) * ny };
    }
    anchors.forEach((pt, index) => {
      const abs = toRegionSpace(pt);
      const rect = new Rect(
        abs.x - controlPointSize,
        abs.y - controlPointSize,
        controlPointSize * 2,
        controlPointSize * 2
      );
      rect.id = `pathPoint_${index}`;
      rect.type = 'pathPoint';
      rect.pointIndex = index;
      rect.localPoint = { ...pt };
      handlers.push(rect);
      rect.node = node;
    });
    for (let i = 0; i < anchors.length - 1; i++) {
      const a = anchors[i];
      const b = anchors[i + 1];
      const absA = toRegionSpace(a);
      const absB = toRegionSpace(b);

      const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
      const abs = toRegionSpace(mid);

      const handler = {
        id: `pathMidPoint_${i}`,
        type: 'pathMidPoint',
        segmentIndex: i,
        localMidPoint: mid,
        node: node,
        center: { x: abs.x, y: abs.y },
        w: controlPointSize * 2,
        h: controlPointSize * 2,
        contains: (x, y) => isNearSegment({ x, y }, absA, absB, 8 * scale),
      };
      handlers.push(handler);
    }
    return handlers;
  }
  return [];
}

export function updatePathPoint(pathData, index, newLocalPoint, _closed = false) {
  const parsed = parseLinearAnchors(pathData);
  if (!parsed) return pathData;
  const { anchors, closed: isClosed } = parsed;
  if (index < 0 || index >= anchors.length) return pathData;
  const newAnchors = anchors.slice();
  newAnchors[index] = { x: newLocalPoint.x, y: newLocalPoint.y };
  return buildPathFromAnchors(newAnchors, isClosed);
}

export function addPathPointAtSegment(pathData, segmentIndex, _closed = false) {
  const parsedSegments = parsePathSegments(pathData);
  if (parsedSegments) {
    const { anchors, segments } = parsedSegments;
    const res = splitSegmentAtMidpoint(anchors, segments, segmentIndex);
    return buildPathFromSegments(res.anchors, res.segments, parsedSegments.closed);
  }
  // fallback linear
  const parsedLinear = parseLinearAnchors(pathData);
  if (!parsedLinear) return pathData;
  const { anchors, closed: isClosed } = parsedLinear;
  if (segmentIndex < 0 || segmentIndex >= anchors.length - 1) return pathData;
  const a = anchors[segmentIndex];
  const b = anchors[segmentIndex + 1];
  const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  const newAnchors = anchors.slice();
  newAnchors.splice(segmentIndex + 1, 0, mid);
  return buildPathFromAnchors(newAnchors, isClosed);
}

// 将直线段转换为曲线段（Q 或 C），或将曲线段还原为直线段
export function convertSegmentType(pathData, segmentIndex, toType = 'Q') {
  const parsed = parsePathSegments(pathData);
  if (!parsed) return pathData;
  const { anchors } = parsed;
  const segments = parsed.segments.slice();
  const seg = segments[segmentIndex];
  if (!seg) return pathData;

  // 基础端点
  const p0 = anchors[seg.from];
  const p1 = anchors[seg.to];
  if (!p0 || !p1) return pathData;

  // 边向量与长度
  const vx = p1.x - p0.x;
  const vy = p1.y - p0.y;
  const len = Math.hypot(vx, vy) || 1;

  // 单位法线（用于轻微偏移，便于看到控制柄）
  const nx = vy / len;
  const ny = -vx / len;
  const k = Math.max(0.001 * len, 0.01);

  if (toType === 'L') {
    // 统一还原为直线
    segments[segmentIndex] = { type: 'L', from: seg.from, to: seg.to };
    return buildPathFromSegments(anchors, segments, parsed.closed);
  }

  // 仅允许对直线或曲线进行互转
  if (seg.type === 'L') {
    if (toType === 'Q') {
      const mid = { x: (p0.x + p1.x) / 2 + nx * k, y: (p0.y + p1.y) / 2 + ny * k };
      segments[segmentIndex] = { type: 'Q', from: seg.from, to: seg.to, c: mid };
    } else if (toType === 'C') {
      const c1 = { x: p0.x + vx / 3 + nx * k, y: p0.y + vy / 3 + ny * k };
      const c2 = { x: p0.x + (vx * 2) / 3 + nx * k, y: p0.y + (vy * 2) / 3 + ny * k };
      segments[segmentIndex] = { type: 'C', from: seg.from, to: seg.to, c1, c2 };
    }
    return buildPathFromSegments(anchors, segments, parsed.closed);
  } else if (seg.type === 'Q' || seg.type === 'C') {
    // 曲线 → 指定类型
    if (toType === 'Q') {
      // 简化为单一控制点，放在中点附近
      const mid = { x: (p0.x + p1.x) / 2 + nx * k, y: (p0.y + p1.y) / 2 + ny * k };
      segments[segmentIndex] = { type: 'Q', from: seg.from, to: seg.to, c: mid };
    } else if (toType === 'C') {
      const c1 =
        seg.type === 'C' ? seg.c1 : { x: p0.x + vx / 3 + nx * k, y: p0.y + vy / 3 + ny * k };
      const c2 =
        seg.type === 'C'
          ? seg.c2
          : { x: p0.x + (vx * 2) / 3 + nx * k, y: p0.y + (vy * 2) / 3 + ny * k };
      segments[segmentIndex] = { type: 'C', from: seg.from, to: seg.to, c1, c2 };
    } else if (toType === 'L') {
      segments[segmentIndex] = { type: 'L', from: seg.from, to: seg.to };
    }
    return buildPathFromSegments(anchors, segments, parsed.closed);
  }

  return pathData;
}

const pathHandlerTypes = {
  pathPoint: 'pathPoint',
  pathControl: 'pathControl',
  pathMidPoint: 'pathMidPoint',
};

// New: dedicated path edit action
export function pathEditAction(hitTestContext) {
  if (hitTestContext.type === EventTypes.down) {
    const handler = hitTestContext.targetHandler;
    const { provider } = hitTestContext;
    const node = handler?.node;
    if (!node || node.type !== TYPES.path) {
      provider.exitPathMode();
      return false;
    }
    if (!handler) return false;
    if (handler.type === 'pathPoint') {
      // Initialize anchor move
      const cache = preparePathEditCache(node.path);
      if (!cache) {
        console.warn('cache miss', cache);
        return false;
      }
      hitTestContext.provider.movingHandler = {
        id: handler.id,
        type: handler.type,
        node: node,
        pointIndex: handler.pointIndex,
        cache: cache,
        point: { ...hitTestContext.point },
        // Store initial stage position of the point for snapping
        startStagePoint: { ...(handler.center || hitTestContext.point) },
      };
      hitTestContext.provider.hoverHandlerId = handler.id;
      return true;
    } else if (handler.type === 'pathControl') {
      // Initialize control move
      const cache = preparePathEditCache(node.path);
      if (!cache) return false;
      hitTestContext.provider.movingHandler = {
        id: handler.id,
        type: handler.type,
        node: node,
        segmentIndex: handler.segmentIndex,
        role: handler.controlRole,
        cache: cache,
        point: { ...hitTestContext.point },
      };
      hitTestContext.provider.hoverHandlerId = handler.id;
      return true;
    } else if (handler.type === 'pathMidPoint') {
      const { action } = hitTestContext.stageContext.providers;
      if (action?.isAltKeyPressed) {
        // Alt: 将直线段转换为曲线；Shift+Alt → 三次贝塞尔；Alt 单独 → 二次贝塞尔；Meta+Alt → 直线
        const toType = action.isMetaKeyPressed ? 'L' : action.isShiftKeyPressed ? 'C' : 'Q';
        const newPath = convertSegmentType(node.path, handler.segmentIndex, toType);
        hitTestContext.stageContext.api.updateNode(node.id, { path: newPath });
      } else {
        // 默认行为：在线段中点处插入新锚点
        // 先清除 alignmentResult 防止残留
        hitTestContext.provider.alignmentResult = null;
        const newPath = addPathPointAtSegment(node.path, handler.segmentIndex, false);
        hitTestContext.stageContext.api.updateNode(node.id, { path: newPath });
      }
      return true;
    }
  } else if (hitTestContext.type === EventTypes.move) {
    const moving = hitTestContext.provider.movingHandler;
    if (!moving) {
      return false;
    }
    if (!pathHandlerTypes[moving.type]) return false;

    const stageContext = hitTestContext.stageContext;
    const node = moving.node;
    const region = stageContext.api.getNodeRegion(node);

    // Basis calculation for transforms
    const TL = region.topLeft;
    const TR = region.topRight;
    const BL = region.bottomLeft;
    const u0 = { x: TR.x - TL.x, y: TR.y - TL.y }; // width vector in stage space
    const v0 = { x: BL.x - TL.x, y: BL.y - TL.y }; // height vector in stage space
    const det = u0.x * v0.y - u0.y * v0.x || 1;

    // Reconstruct basis for snapping (converting other anchors to stage space)
    // NOTE: We must match getInteractivePathHandlers' normalization logic precisely.
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    if (moving.cache && moving.cache.anchors) {
      moving.cache.anchors.forEach((p) => {
        minX = Math.min(minX, p.x);
        minY = Math.min(minY, p.y);
        maxX = Math.max(maxX, p.x);
        maxY = Math.max(maxY, p.y);
      });
      // We could include segments too but anchors are primary.
      // Let's include segments to match exactly if possible, but anchors are enough for the bounds if it's a polygon.
      // For consistency with getInteractivePathHandlers:
      if (moving.cache.segments) {
        moving.cache.segments.forEach((s) => {
          if (s.type === 'C') {
            minX = Math.min(minX, s.c1.x, s.c2.x);
            minY = Math.min(minY, s.c1.y, s.c2.y);
            maxX = Math.max(maxX, s.c1.x, s.c2.x);
            maxY = Math.max(maxY, s.c1.y, s.c2.y);
          } else if (s.type === 'Q') {
            minX = Math.min(minX, s.c.x);
            minY = Math.min(minY, s.c.y);
            maxX = Math.max(maxX, s.c.x);
            maxY = Math.max(maxY, s.c.y);
          }
        });
      }
    }

    const basisW = node.w && node.w > 0 ? node.w : Math.max(1, maxX - minX);
    const basisH = node.h && node.h > 0 ? node.h : Math.max(1, maxY - minY);
    const basisX = node.w && node.w > 0 ? 0 : minX;
    const basisY = node.h && node.h > 0 ? 0 : minY;

    const toRegionSpace = (local) => {
      const nx = basisW ? (local.x - basisX) / basisW : 0;
      const ny = basisH ? (local.y - basisY) / basisH : 0;
      // Bilinear interpolation
      return {
        x: TL.x + (TR.x - TL.x) * nx + (BL.x - TL.x) * ny,
        y: TL.y + (TR.y - TL.y) * nx + (BL.y - TL.y) * ny,
      };
    };

    let dxStage = hitTestContext.point.x - moving.point.x;
    let dyStage = hitTestContext.point.y - moving.point.y;

    // --- Snapping Logic ---
    if (moving.type === 'pathPoint' && moving.cache && moving.startStagePoint) {
      const threshold = 6 / (stageContext.providers.scale.scale || 1);
      const currentStage = {
        x: moving.startStagePoint.x + dxStage,
        y: moving.startStagePoint.y + dyStage,
      };

      let snappedX = null;
      let snappedY = null;
      const lines = [];

      // Iterate other anchors
      moving.cache.anchors.forEach((anchor, idx) => {
        if (idx === moving.pointIndex) return;
        const abs = toRegionSpace(anchor);

        if (Math.abs(abs.x - currentStage.x) < threshold) {
          snappedX = abs.x;
          lines.push({
            type: 'vertical',
            left: abs.x,
            top: Math.min(abs.y, currentStage.y),
            size: Math.abs(abs.y - currentStage.y),
            source: 'obj',
          });
        }
        if (Math.abs(abs.y - currentStage.y) < threshold) {
          snappedY = abs.y;
          lines.push({
            type: 'horizontal',
            left: Math.min(abs.x, currentStage.x),
            top: abs.y,
            size: Math.abs(abs.x - currentStage.x),
            source: 'obj',
          });
        }
      });

      if (snappedX !== null) {
        dxStage = snappedX - moving.startStagePoint.x;
      }
      if (snappedY !== null) {
        dyStage = snappedY - moving.startStagePoint.y;
      }

      // Update alignment result for rendering
      if (lines.length > 0) {
        hitTestContext.provider.alignmentResult = { lines };
      } else {
        hitTestContext.provider.alignmentResult = null;
      }
    } else {
      hitTestContext.provider.alignmentResult = null;
    }

    // Solve [u0 v0] * [a b]^T = dStage
    const a = (dxStage * v0.y - dyStage * v0.x) / det;
    const b = (-dxStage * u0.y + dyStage * u0.x) / det;
    const w = moving.node.w || 1;
    const h = moving.node.h || 1;
    const moveX = a * w;
    const moveY = b * h;

    if (moving.type === 'pathPoint') {
      const result = applyMoveAnchor(moving.cache, moving.pointIndex, moveX, moveY);
      hitTestContext.stageContext.api.updateNode(moving.node.id, { path: result.path });
      return true;
    } else if (moving.type === 'pathControl') {
      const { action } = hitTestContext.stageContext.providers;
      const result = applyMoveControl(
        moving.cache,
        moving.segmentIndex,
        moving.role,
        moveX,
        moveY,
        { symmetric: !!action?.isShiftKeyPressed }
      );
      hitTestContext.stageContext.api.updateNode(moving.node.id, { path: result.path });
      return true;
    }
  }
  return false;
}
